import { z } from 'zod'
import { LocaleSchema } from './auth'

// The public "contact us" form (POST /api/public/contact). Nothing is
// stored: the enquiry is emailed to whoever handles that kind of enquiry.

export const CONTACT_ENQUIRY_TYPES = ['training', 'membership', 'other'] as const
export type ContactEnquiryType = (typeof CONTACT_ENQUIRY_TYPES)[number]

export const CONTACT_MESSAGE_MAX = 1000

// No control characters except line breaks and tabs in free text: they have
// no business in a message and are how header-injection and log-forging
// attempts are made.
const CONTROL = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/
const SINGLE_LINE = /[\u0000-\u001F\u007F]/

export const ContactEnquirySchema = z.object({
  name: z.string().trim().min(1).max(100).refine((v) => !SINGLE_LINE.test(v), 'Not a valid name'),
  email: z.string().trim().toLowerCase().email().max(254),
  phone: z
    .string()
    .trim()
    .max(40)
    .regex(/^([+\d][\d\s()-]{4,})?$/, 'Not a valid phone number')
    .nullish()
    .transform((v) => v || null),
  enquiryType: z.enum(CONTACT_ENQUIRY_TYPES),
  message: z
    .string()
    .trim()
    .min(1)
    .max(CONTACT_MESSAGE_MAX)
    .refine((v) => !CONTROL.test(v), 'The message contains characters that are not allowed'),
  // The language the form was shown in; the email to the club follows it.
  locale: LocaleSchema.default('fi'),
  turnstileToken: z.string().max(2048).optional(),
  // Honeypot: hidden from people, filled in by naive bots. Anything in it
  // and the enquiry is quietly dropped (routes/publicContact.ts).
  website: z.string().max(500).optional(),
})
export type ContactEnquiryInput = z.input<typeof ContactEnquirySchema>
